import { DprFormulaValues, FormulaExpression } from '@dpr-details/models';
import { ExtendedDataType } from '@shared/models';
import {
  StatusTrackerColumn,
  StatusTrackerColumnFormula,
  StatusTrackerWithData,
  TableOfColumnWithData,
} from '@shared/models/project-setup';

/**
 * What a blank input is substituted as in a formula expression. It reads as 0,
 * but dividing by it gives 0 instead of an error: a blank divisor means nothing
 * has been entered yet. The API substitutes the same token; the database's
 * reporting functions keep their own rule, where a blank is 0.
 */
export const FormulaBlankOperand = '_';

/**
 * What a formula cell shows when it divides by a zero that is not blank: the
 * spreadsheet error users already know. The next DPR reads it as a blank.
 */
export const FormulaDivisionByZeroError = '#DIV/0!';

/** The decimals a formula cell shows in a grid. */
export const FormulaDisplayPrecision = 2;

/**
 * The decimals a formula value keeps when the DPR after it starts from it, as the
 * API keeps its previous values.
 */
export const FormulaPreviousValuePrecision = 10;

/**
 * Whether a cell holds the division-by-zero error rather than a value, so
 * whatever reads the cell as an input takes it as blank, as the API does.
 */
export function isDivisionByZeroError(value: unknown): boolean {
  return value === FormulaDivisionByZeroError;
}

/**
 * Whether an edited row divides by zero in a formula cell that showed no such error
 * in any of the earlier values. An error that was already there was not caused by
 * the edit.
 */
export function hasNewDivisionByZeroError(
  editedValues: DprFormulaValues,
  ...earlierValues: DprFormulaValues[]
): boolean {
  return [...editedValues].some(([rowId, values]) =>
    [...values].some(
      ([columnId, value]) =>
        isDivisionByZeroError(value) &&
        !earlierValues.some((earlier) =>
          isDivisionByZeroError(earlier.get(rowId)?.get(columnId))
        )
    )
  );
}

/**
 * The column ids a formula expression references, read with a boundary-safe
 * pattern rather than by splitting the expression into whitespace-separated
 * tokens, so an expression written without spaces around its operators resolves
 * too, and `$12` is never read out of `$123` or `$12abc`. Only a saved column can
 * be referenced, so a non-numeric id is not a reference.
 */
export function parseFormulaColumnIds(expression: string): number[] {
  const ids = new Set<number>();

  for (const match of (expression ?? '').matchAll(/\$(\d+)(?!\w)/g)) {
    ids.add(+match[1]);
  }

  return [...ids];
}

export function getTableOfColumnFormulas(
  tableOfColumn: TableOfColumnWithData
): FormulaExpression[] {
  return (tableOfColumn?.tableOfColumnItems ?? [])
    .filter(
      (item) =>
        item.dataTypeId === ExtendedDataType.Formula &&
        !String.isNullOrWhiteSpace(item.formula?.expression)
    )
    .map((item) => ({
      columnId: +item.id,
      expression: item.formula.expression,
    }));
}

/**
 * The status tracker model declares formulas both nested on each column and
 * flattened on the status tracker, like its carry-overs, so fall back to the flat
 * list rather than treating a missing nested record as "no formula".
 */
function getStatusTrackerColumnFormula(
  statusTracker: StatusTrackerWithData,
  column: StatusTrackerColumn
): StatusTrackerColumnFormula | undefined {
  if (column?.formula) {
    return column.formula;
  }

  return statusTracker?.formulas?.find(
    (formula) => +formula.statusTrackerColumnId === +column?.id
  );
}

export function getStatusTrackerFormulas(
  statusTracker: StatusTrackerWithData
): FormulaExpression[] {
  return (statusTracker?.columns ?? []).flatMap((column) => {
    const expression = getStatusTrackerColumnFormula(
      statusTracker,
      column
    )?.expression;

    return column.dataTypeId === ExtendedDataType.Formula &&
      !String.isNullOrWhiteSpace(expression)
      ? [{ columnId: +column.id, expression }]
      : [];
  });
}

/**
 * The formula columns that reach their own value, directly or through other
 * formulas. Such a column is a running total that reads its previous value from
 * the DPR before, which makes it an accumulated column.
 */
export function getSelfReferencingFormulaColumnIds(
  formulas: FormulaExpression[]
): Set<number> {
  return findSelfReferencingColumnIds(formulas, mapFormulaReferences(formulas));
}

/**
 * The columns that read the DPR before: the carry-overs, running totals, and
 * formulas that reach one of them, directly or through other formulas.
 */
export function getHistoryColumnIds(
  formulas: FormulaExpression[],
  carryOverColumnIds: Set<number>
): Set<number> {
  const referencedIds = mapFormulaReferences(formulas);
  const historyColumnIds = new Set([
    ...carryOverColumnIds,
    ...findSelfReferencingColumnIds(formulas, referencedIds),
  ]);

  formulas
    .filter(({ columnId }) =>
      [...getColumnsReadByFormula(referencedIds, columnId)].some((id) =>
        historyColumnIds.has(id)
      )
    )
    .forEach(({ columnId }) => historyColumnIds.add(columnId));

  return historyColumnIds;
}

export function getFormulaInputColumnIds(
  formulas: FormulaExpression[]
): Set<number> {
  const formulaColumnIds = new Set(formulas.map((formula) => formula.columnId));

  return new Set(
    formulas
      .flatMap((formula) => parseFormulaColumnIds(formula.expression))
      .filter((columnId) => !formulaColumnIds.has(columnId))
  );
}

/**
 * The calendar day a DPR date belongs to, as `YYYY-MM-DD`.
 *
 * Days rather than instants, because the formula engine asks whether a card is
 * from an earlier day, and one day can arrive in several forms: a string without
 * a zone, read as local time, one carrying `Z`, read as UTC, or a `Date`. As
 * instants, the same day in two of those forms is hours apart.
 */
export function getCalendarDay(value: Date | string | null): string | null {
  if (value == null) {
    return null;
  }

  if (typeof value === 'string') {
    const isoDay = /^(\d{4}-\d{2}-\d{2})/.exec(value);
    if (isoDay !== null) {
      return isoDay[1];
    }
  }

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  // The local calendar parts, because a Date is an instant and reading one in
  // UTC — as `toISOString()` does — lands a day early at any offset ahead of
  // UTC: local midnight on the 27th is 22:00 on the 26th. That disagrees with
  // the string branch above for the same day, and a DPR whose day is read
  // early stops counting the day before it as earlier than itself.
  return [
    parsed.getFullYear(),
    String(parsed.getMonth() + 1).padStart(2, '0'),
    String(parsed.getDate()).padStart(2, '0'),
  ].join('-');
}

function mapFormulaReferences(
  formulas: FormulaExpression[]
): Map<number, number[]> {
  return new Map(
    formulas.map((formula) => [
      formula.columnId,
      parseFormulaColumnIds(formula.expression),
    ])
  );
}

function findSelfReferencingColumnIds(
  formulas: FormulaExpression[],
  referencedIds: Map<number, number[]>
): Set<number> {
  return new Set(
    formulas
      .filter(({ columnId }) =>
        getColumnsReadByFormula(referencedIds, columnId).has(columnId)
      )
      .map(({ columnId }) => columnId)
  );
}

/**
 * Every column a formula needs to be worked out: the ones it names, plus the
 * ones those formulas name, and so on. With $5 = $3 * 2 and $3 = $1 + $2,
 * $5 reads $3, $1 and $2.
 */
function getColumnsReadByFormula(
  referencedIds: Map<number, number[]>,
  formulaColumnId: number
): Set<number> {
  const readIds = new Set<number>();
  const pendingIds = [...(referencedIds.get(formulaColumnId) ?? [])];

  while (pendingIds.length) {
    const columnId = pendingIds.pop();

    // Formulas that read each other in a cycle would otherwise loop forever.
    if (!readIds.has(columnId)) {
      readIds.add(columnId);
      pendingIds.push(...(referencedIds.get(columnId) ?? []));
    }
  }

  return readIds;
}
